<template>
  <div class="flyer-swiper-wrap" :class="{ 'flyer-swiper-instagram': variant === 'instagram' }">
    <Swiper
      class="flyer-swiper"
      :modules="modules"
      :slides-per-view="1"
      :space-between="0"
      :loop="normalizedImages.length > 1"
      :auto-height="true"
      :pagination="{ clickable: true }"
      @swiper="handleSwiper"
    >
      <SwiperSlide v-for="(image, imageIndex) in normalizedImages" :key="`${image}-${imageIndex}`">
        <div class="flyer-swiper-slide">
          <div v-if="!isImageLoaded(image)" class="flyer-image-placeholder" aria-hidden="true">
            <i class="ri-image-line" aria-hidden="true"></i>
          </div>
          <img
            class="flyer-swiper-image"
            :class="{ 'is-loaded': isImageLoaded(image) }"
            :src="image"
            :alt="`Flyer image ${imageIndex + 1}`"
            loading="lazy"
            @load="markImageLoaded(image)"
            @error="markImageLoaded(image)"
          />
        </div>
      </SwiperSlide>
    </Swiper>
    <template v-if="normalizedImages.length > 1">
      <button class="flyer-swiper-nav flyer-swiper-nav-prev" type="button" aria-label="Previous image" @click="slidePrev">
        <i class="ri-arrow-left-s-line" aria-hidden="true"></i>
      </button>
      <button class="flyer-swiper-nav flyer-swiper-nav-next" type="button" aria-label="Next image" @click="slideNext">
        <i class="ri-arrow-right-s-line" aria-hidden="true"></i>
      </button>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'
import 'swiper/css/pagination'

const props = defineProps({
  images: { type: Array, default: () => [] },
  variant: { type: String, default: 'default' }
})

const modules = [Pagination]
const swiperInstance = ref(null)
const loadedImages = ref(new Set())

const normalizedImages = computed(() => {
  const images = props.images.filter(Boolean)
  return images.length ? images : ['/assets/placeholders/no-feeds-found.svg']
})

const handleSwiper = (swiper) => {
  swiperInstance.value = swiper
}

const isImageLoaded = (image) => loadedImages.value.has(image)

const markImageLoaded = (image) => {
  const nextLoadedImages = new Set(loadedImages.value)
  nextLoadedImages.add(image)
  loadedImages.value = nextLoadedImages
}

watch(normalizedImages, () => {
  loadedImages.value = new Set()
})

const slidePrev = () => {
  swiperInstance.value?.slidePrev()
}

const slideNext = () => {
  swiperInstance.value?.slideNext()
}
</script>

<style scoped>
.flyer-swiper-wrap {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: inherit;
  background: #f8fafc;
}

.flyer-swiper {
  position: relative;
  z-index: 1;
  width: 100%;
  height: auto;
}

.flyer-swiper-slide {
  position: relative;
  min-height: min(18rem, 58vh);
  overflow: hidden;
  background: #f8fafc;
}

.flyer-image-placeholder {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  min-height: min(18rem, 58vh);
  background:
    linear-gradient(90deg, rgb(226 232 240 / 0.72), rgb(248 250 252), rgb(226 232 240 / 0.72));
  background-size: 220% 100%;
  color: #94a3b8;
  animation: flyer-image-loading 1.15s ease-in-out infinite;
}

.flyer-image-placeholder i {
  font-size: 2rem;
  opacity: 0.72;
}

.flyer-swiper-image {
  display: block;
  width: 100%;
  height: auto;
  max-height: calc(100vh - 13rem);
  object-fit: contain;
  opacity: 0;
  padding: 0;
  transition: opacity 0.18s ease;
}

.flyer-swiper-image.is-loaded {
  opacity: 1;
}

.flyer-swiper-instagram .flyer-swiper-slide,
.flyer-swiper-instagram .flyer-image-placeholder {
  min-height: 0;
}

.flyer-swiper-instagram .flyer-swiper-image {
  display: block;
  width: 100%;
  height: auto;
  max-height: none;
  object-fit: initial;
}

.flyer-swiper-nav {
  position: absolute;
  top: 50%;
  z-index: 3;
  display: grid;
  place-items: center;
  width: 1.8rem;
  height: 1.8rem;
  border: 1px solid rgb(255 255 255 / 0.5);
  border-radius: 9999px;
  background: rgb(15 23 42 / 0.58);
  color: #fff;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 18px rgb(15 23 42 / 0.24);
  transform: translateY(-50%);
  transition: transform 0.18s ease, background 0.18s ease, border-color 0.18s ease;
}

.flyer-swiper-nav i {
  font-size: 1rem;
  line-height: 1;
}

.flyer-swiper-nav-prev {
  left: 0.85rem;
}

.flyer-swiper-nav-next {
  right: 0.85rem;
}

.flyer-swiper-nav:hover {
  border-color: rgb(255 255 255 / 0.82);
  background: rgb(15 23 42 / 0.74);
  transform: translateY(-50%) scale(1.04);
}

:deep(.swiper-pagination) {
  bottom: 1rem;
}

:deep(.swiper-pagination-bullet) {
  width: 0.48rem;
  height: 0.48rem;
  background: rgb(15 23 42 / 0.28);
  opacity: 1;
  transition: width 0.18s ease, background 0.18s ease;
}

:deep(.swiper-pagination-bullet-active) {
  width: 1.55rem;
  border-radius: 9999px;
  background: #2563eb;
}

@keyframes flyer-image-loading {
  0% {
    background-position: 100% 0;
  }

  100% {
    background-position: -100% 0;
  }
}

@media (max-width: 640px) {
  .flyer-swiper-slide,
  .flyer-image-placeholder {
    min-height: min(15rem, 54vh);
  }

  .flyer-swiper-nav {
    width: 1.65rem;
    height: 1.65rem;
  }

  .flyer-swiper-nav i {
    font-size: 0.92rem;
  }

  .flyer-swiper-nav-prev {
    left: 0.55rem;
  }

  .flyer-swiper-nav-next {
    right: 0.55rem;
  }
}
</style>
